import type { Timestamp } from "@bufbuild/protobuf";
import { Resolution } from "gitpod-next-api/gitpod/v1/usage_pb";
import { createParser } from "nuqs";

export const TIME_RANGES: TimeRange[] = [
    { label: "Last Day", days: 1, value: "last-day" },
    { label: "Last Week", days: 7, value: "last-week" },
    { label: "Last 14 Days", days: 14, value: "last-14-days" },
    { label: "Last Month", days: 30, value: "last-month" },
    { label: "Last 6 Months", days: 180, value: "last-6-months" },
    { label: "Last Year", days: 365, value: "last-year" },
];

export type TimeRange = {
    label: string;
    days: number;
    value: string;
};

export type SelectableTimeRange = TimeRange & {
    disabled?: boolean;
};

export const parseAsTimeRange = createParser({
    parse(queryValue) {
        const range = TIME_RANGES.find((range) => range.value === queryValue);
        if (!range) {
            throw new Error(`Invalid time range: ${queryValue}`);
        }
        return range;
    },
    serialize(value) {
        return value.value;
    },
});

const VIEWS = ["activeUsers", "activeEnvironments", "environmentRuntime"] as const;
export type View = (typeof VIEWS)[number];

export const parseAsView = createParser<View>({
    parse(queryValue) {
        if (!queryValue || !VIEWS.includes(queryValue as View)) {
            throw new Error(`Invalid view: ${queryValue}`);
        }
        return queryValue as View;
    },
    serialize(value) {
        return value;
    },
});

export const formatChartDate = (
    date: Date,
    resolution: Resolution,
    startTimestamp: Timestamp,
    endTimestamp: Timestamp,
) => {
    const includeYear = startTimestamp.toDate().getFullYear() !== endTimestamp.toDate().getFullYear();
    const intlFormatOptions: Intl.DateTimeFormatOptions = {
        year: includeYear ? "numeric" : undefined,
        month: "short",
        day: "numeric",
    };

    switch (resolution) {
        case Resolution.HOURLY: {
            const format = new Intl.DateTimeFormat("en-US", {
                ...intlFormatOptions,
                hour: "numeric",
                minute: "numeric",
            });

            return format.format(date);
        }
        case Resolution.WEEKLY: {
            const format = new Intl.DateTimeFormat("en-US", intlFormatOptions);

            return `Week of ${format.format(date)}`;
        }
        case Resolution.MONTHLY: {
            const format = new Intl.DateTimeFormat("en-US", {
                ...intlFormatOptions,
                month: "long",
                day: undefined,
            });

            return `${format.format(date)}`;
        }
        default: {
            const format = new Intl.DateTimeFormat("en-US", intlFormatOptions);

            return format.format(date);
        }
    }
};
